'use client';

/**
 * @fileoverview Shared Discover wall-clock (WEB-779 / PAR-05).
 *
 * One `now` + the city's IANA zone for the whole Discover surface. The list
 * groups, row countdown, NOW marker, and provenance strip all read this
 * instant so they cannot drift by a constant offset. Cadence matches
 * {@link DiscoverNowMarker} / {@link DiscoverCityClock}: once per minute,
 * aligned to the wall-clock minute boundary.
 *
 * `now` is `null` until mount (and stays `null` without a provider) so SSR
 * and the first client paint stay hydration-safe. Stories and isolated tests
 * that omit the provider keep their existing injected-`now` props.
 *
 * @since WEB-779
 */

import * as React from 'react';

import { CITY_RECORDS, type CityCode } from '@loci/city-editions';

import { getCityTimezone } from '@/lib/home/cityTimezone';

const TICK_INTERVAL_MS = 60_000;

export interface DiscoverClockValue {
  /** Shared instant, or `null` before mount / without a provider. */
  readonly now: Date | null;
  /** City IANA zone — the timeline spine zone. */
  readonly timeZone: string;
}

const DEFAULT_CLOCK: DiscoverClockValue = {
  now: null,
  // COR-155: the MVP market's zone, read off its city record.
  timeZone: CITY_RECORDS.CLT.timeZone,
};

const DiscoverClockContext = React.createContext<DiscoverClockValue>(DEFAULT_CLOCK);

export interface DiscoverClockProviderProps {
  /** Resolved city code — selects the spine IANA zone. */
  readonly cityCode: CityCode;
  /**
   * Optional fixed instant for deterministic tests / Storybook. When supplied
   * the clock renders immediately and does not tick.
   */
  readonly now?: Date;
  readonly children: React.ReactNode;
}

/**
 * Provide one timeline clock to the Discover subtree.
 */
export function DiscoverClockProvider({
  cityCode,
  now: frozenNow,
  children,
}: DiscoverClockProviderProps): React.ReactElement {
  const timeZone = getCityTimezone(cityCode);
  const [liveNow, setLiveNow] = React.useState<Date | null>(frozenNow ?? null);

  React.useEffect(() => {
    if (frozenNow) {
      setLiveNow(frozenNow);
      return;
    }
    const tick = (): void => setLiveNow(new Date());
    tick();
    let intervalId: number | undefined;
    const msToNextMinute = TICK_INTERVAL_MS - (Date.now() % TICK_INTERVAL_MS);
    const timeoutId = window.setTimeout(() => {
      tick();
      intervalId = window.setInterval(tick, TICK_INTERVAL_MS);
    }, msToNextMinute);
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [frozenNow]);

  const value = React.useMemo<DiscoverClockValue>(
    () => ({ now: frozenNow ?? liveNow, timeZone }),
    [frozenNow, liveNow, timeZone],
  );

  return <DiscoverClockContext.Provider value={value}>{children}</DiscoverClockContext.Provider>;
}

/**
 * Read the shared Discover clock. Returns the default (`now: null`,
 * Charlotte zone) when rendered outside {@link DiscoverClockProvider}.
 */
export function useDiscoverClock(): DiscoverClockValue {
  return React.useContext(DiscoverClockContext);
}
